import { memo, useReducer } from "react";

import { clone, hasValue } from "./general";
import { log as internalLog } from "./logger";

export function Memoize<TProps>(
	component: (props: TProps) => JSX.Element,
	ignoreProps: (string & keyof TProps)[] = [],
	ignoreFunctions = true,
	log = false,
) {
	return memo(component, areEqual);

	function areEqual(prevProps: TProps, nextProps: TProps) {
		const prev = { ...prevProps };
		const next = { ...nextProps };
		for (const ignore of ["children", ...ignoreProps]) {
			prev[ignore] = undefined;
			next[ignore] = undefined;
		}
		const difference = areDifferent(prev, next);
		if (log && difference) internalLog(difference);
		return !difference;
	}

	/**
	 * Returns null if items are equal
	 * If items are not equal, returns "." delimited prop where are not equal
	 * @param o1
	 * @param o2
	 * @returns
	 */
	function areDifferent<T>(o1: T, o2: T, lookDeep = true): string {
		if (o1 === o2) return null;
		if (!hasValue(o1) && !hasValue(o2)) return null;
		if (!hasValue(o1) || !hasValue(o2)) return `${o1},${o2}`;
		switch (typeof o1) {
			case "function":
				if (ignoreFunctions) return null;
				break;
			case "object":
				if (!lookDeep) {
					const str1 = JSON.stringify(o1);
					const str2 = JSON.stringify(o2);
					return str1 === str2 ? null : `${str1},${str2}`;
				}
				break;
			default:
				return `${o1},${o2}`;
		}

		const keys = Object.keys(o1) as (string & keyof T)[];
		const p2Keys = Object.keys(o2) as (string & keyof T)[];
		if (keys.length !== p2Keys.length) return "keys";
		for (const key of keys) {
			const difference = areDifferent(o1[key], o2[key], log);
			if (difference) return key + "." + difference;
		}
		return null;
	}
}

/** Wraps React's useReducer with a basic reducer that just takes actions of individual property updates */
export function usePropReducer<T>(initialState: T, initializer?: () => T) {
	const [state, dispatch] = useReducer(simpleReducer, initialState, initializer);
	const update = (...props: PropUpdate<T>[]) => props.forEach(dispatch);
	return [state, update] as [T, (...props: PropUpdate<T>[]) => void];
	function simpleReducer(state: T, action: PropUpdate<T>) {
		const newState = clone(state);
		newState[action.name] = action.value;
		return newState;
	}
}
type PropUpdate<T> = { name: keyof T; value: any };

/** Wraps React's useReducer with a basic reducer that just takes actions of individual property updates */
export function usePartialReducer<T>(initialState: T, initializer?: () => T) {
	return useReducer(partialReducer, initialState, initializer);

	function partialReducer(state: T, action: Partial<T>) {
		return { ...state, ...action };
	}
}
